import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFilteredUsers, putWorkspace } from '../actions/workspaceAction';
import { fetchEnvironmentAccess, setEnvironmentAccess } from '../api/api';
import {
    Grid2, Box, Typography, Modal, Avatar, Autocomplete, Paper, Snackbar, Alert, Select, MenuItem,
    Collapse, Chip, Tooltip
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddButton from "../themes/overrides/AddButton";
import ModalBox from "../themes/overrides/ModalBox";
import TextFieldInput from "../themes/overrides/TextFieldInput";
import ProfileAddIcon from "../assets/images/profile-add.svg";
import copyLinkIcon from "../assets/images/copy-link.svg";
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { ReactComponent as RemoveIcon } from '../assets/images/icons/remove_icon.svg'
import PropTypes from 'prop-types';
import OutlinedAddButton from '../themes/overrides/OutlinedAddButton';

const ManageUsersModal = ({ open, onClose, selectedType, onSuccess }) => {
    const dispatch = useDispatch();
    const [currentType, setCurrentType] = useState(selectedType);
    const [emailInput, setEmailInput] = useState('');
    const filteredUsers = useSelector((state) => state.workspace.filteredUsers);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedDeleteUser, setSelectedDeleteUser] = useState([]);
    const currentUser = JSON.parse(localStorage.getItem('userInfo'));
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedRoles, setSelectedRoles] = useState({});

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const currentWorkspace = useSelector((state) => state.workspace.workspaceInfo);

    /* ---------------- per-environment access ----------------
     * A member's workspace role applies in every environment unless one names
     * a different role. The API returns each member's overrides plus the
     * EFFECTIVE role per environment, which is what the selects below show —
     * so an inherited role and an explicitly set one look the same to read,
     * and only differ in whether "Inherit" is the current value.
     */
    const [environments, setEnvironments] = useState([]);
    const [accessByUser, setAccessByUser] = useState({});
    const [expandedUser, setExpandedUser] = useState(null);
    const [savingAccess, setSavingAccess] = useState(null);

    const workspaceId = currentType?._id;

    const loadAccess = async () => {
        if (!workspaceId) return;
        try {
            const res = await fetchEnvironmentAccess(workspaceId);
            const data = res?.data?.data;
            setEnvironments(data?.environments ?? []);
            setAccessByUser(
                Object.fromEntries((data?.members ?? []).map((m) => [String(m.userId), m])),
            );
        } catch (error) {
            // Not fatal: the workspace-role controls above still work, so leave
            // the environment section out rather than blocking the whole modal.
            setEnvironments([]);
        }
    };

    useEffect(() => {
        if (open && workspaceId) loadAccess();
        if (!open) setExpandedUser(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, workspaceId]);

    const handleEnvironmentRoleChange = async (userId, environmentKey, value) => {
        setSavingAccess(`${userId}:${environmentKey}`);
        try {
            const res = await setEnvironmentAccess(workspaceId, {
                userId,
                // The default environment is addressed by omitting it, exactly
                // as everywhere else in this feature.
                environment: environmentKey === 'production' ? undefined : environmentKey,
                role: value === 'inherit' ? null : value,
            });
            if (res?.data?.status !== 'success') {
                // e.g. demoting the last admin of production, which the API refuses.
                showSnackbar(res?.data?.message ?? 'Could not change access', 'error');
            } else {
                showSnackbar(
                    value === 'inherit'
                        ? 'Environment role cleared — this member now follows their workspace role'
                        : 'Environment access updated',
                    'success',
                );
            }
            await loadAccess();
        } catch (error) {
            showSnackbar(
                error?.response?.data?.message ?? 'Could not change access',
                'error',
            );
        } finally {
            setSavingAccess(null);
        }
    };

    useEffect(() => {
        if (selectedType) {
            setCurrentType(currentWorkspace?.data);
        }
    }, [currentWorkspace, selectedType]);


    const debounce = (func, wait) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    };

    const handleEmailSearch = debounce((value) => {
        if (value.trim()) {
            dispatch(getFilteredUsers(value));
        }
    }, 300);

    const handleEmailChange = (event, newValue) => {
        setSelectedUsers(newValue);
        const newRoles = {};
        newValue.forEach(user => {
            if (!selectedRoles[user._id]) {
                newRoles[user._id] = 'viewer';
            }
        });
        setSelectedRoles(prev => ({ ...prev, ...newRoles }));
    };

    const handleInputChange = (event, newInputValue) => {
        setEmailInput(newInputValue);
        handleEmailSearch(newInputValue);
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            const userData = {
                user: userId,
                role: newRole
            };
            const workspaceId = currentType._id;
            const type = 'updateRole'; 
            await dispatch(putWorkspace(workspaceId, userData, type))
                .then(() => {
                    onSuccess?.();
                    setCurrentType(prev => ({
                        ...prev,
                        user_details: prev.user_details.map(user =>
                            user._id === userId
                                ? { ...user, role: { ...user.role, role: newRole } }
                                : user
                        )
                    }));
                    showSnackbar('User role updated successfully!', 'success');
                });
        } catch (error) {
            showSnackbar('Failed to update user role', 'error');
        }
    };



    const handleRemoveUserPopup = (user) => {
        setSelectedDeleteUser([user]);
        setDeleteModalOpen(true);
    }

    const handleAddUsers = async () => {
        try {
            const userData = selectedUsers.map(user => ({
                user: user._id,
                role: selectedRoles[user._id]
            }))
            const workspaceId = currentType._id;
            const type = 'addUsers'
            await dispatch(putWorkspace(workspaceId, userData, type))
                .then(() => {
                    onSuccess?.();
                    setSelectedUsers([]);
                    setSelectedRoles({});
                    showSnackbar('User added to workspace successfully!', 'success');
                    //onClose();
                })
        } catch (error) {
            showSnackbar('Failed to add user to workspace', 'error');
            console.error('Failed to add users, please try again')
        }
    };

    const handleDeleteUsers = async () => {
        try {
            const userIds = selectedDeleteUser.map(user => user._id);
            const workspaceId = currentType._id;
            const type = 'removeUsers'
            await dispatch(putWorkspace(workspaceId, userIds, type))
                .then(() => {
                    setSelectedDeleteUser([]);
                    setDeleteModalOpen(false);
                    onSuccess?.();
                    showSnackbar('User removed from workspace successfully!', 'success');
                })
        } catch (error) {
            showSnackbar('Failed to remove user from the workspace', 'error');
            console.error('Failed to remove users, please try again')
        }
    }

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const CustomChip = ({ email, user, onRoleChange, onDelete }) => (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                m: 0.25,
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '16px',
                height: '32px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                    borderColor: '#bdbdbd'
                }
            }}
        >
            <Typography
                sx={{
                    ml: 1.5,
                    mr: 1,
                    fontSize: '0.875rem',
                    fontWeight: 500
                }}
            >
                {email}
            </Typography>
            <Box
                component="span"
                onClick={(e) => e.stopPropagation()}
                sx={{
                    mr: 0.5,
                    position: 'relative',
                }}
            >
                <Select
                    value={selectedRoles[user._id]}
                    onChange={(e) => onRoleChange(user._id, e.target.value)}
                    style={{
                        fontSize: '0.813rem',
                        cursor: 'pointer',
                        padding: '2px 1px 2px 8px',
                        color: '#616161',
                        fontWeight: 500,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                ml: 2
                            }
                        }
                    }}
                >
                    <MenuItem
                        value="viewer"
                        sx={{
                            fontSize: '0.813rem',
                            minHeight: '28px',
                            color: '#757575',
                            py: 0.5
                        }}
                    >
                        View
                    </MenuItem>
                    <MenuItem
                        value="editor"
                        sx={{
                            fontSize: '0.813rem',
                            minHeight: '28px',
                            color: '#757575',
                            py: 0.5
                        }}
                    >
                        Edit
                    </MenuItem>
                    <MenuItem
                        value="admin"
                        sx={{
                            fontSize: '0.813rem',
                            minHeight: '28px',
                            color: '#757575',
                            py: 0.5
                        }}
                    >
                        Admin
                    </MenuItem>
                </Select>
            </Box>
            <Box
                component="span"
                onClick={onDelete}
                sx={{
                    cursor: 'pointer',
                    p: '4px 8px',
                    mr: 0.5,
                    color: '#757575',
                    fontSize: '1rem',
                    lineHeight: 1,
                    transition: 'all 0.2s ease',
                    borderRadius: '50%',
                    '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)',
                        color: '#424242'
                    }
                }}
            >
                ×
            </Box>
        </Box>
    );

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <ModalBox sx={{ width: '560px', maxWidth: '95%' }}>
                    <Typography variant="h2" sx={{ mb: 1 }}>
                        Manage Users
                    </Typography>
                    <Grid2>
                        <Typography sx={{ mt: isMobile ? 2 : 6 }}>
                            <span className="font-normal">Invite people to workspace </span>
                            "{currentType?.name}"
                        </Typography>
                        <Grid2>
                            <Autocomplete
                                sx={{
                                    mt: 2,
                                    '& .MuiOutlinedInput-root': { padding: '6px' }
                                }}
                                multiple
                                value={selectedUsers}
                                onChange={handleEmailChange}
                                inputValue={emailInput}
                                onInputChange={handleInputChange}
                                options={
                                    filteredUsers?.filter(user => {
                                        const excludedIds = [
                                            currentType?.createdBy?.[0]?._id,
                                            ...(currentType?.user_details?.map(user => user._id) || [])
                                        ].filter(Boolean);

                                        return !excludedIds.includes(user._id);
                                    }) || []
                                }
                                noOptionsText="No user found"
                                getOptionLabel={(option) => option.email}
                                getOptionDisabled={(option) =>
                                    selectedUsers.some(user => user.email === option.email)
                                }
                                popupIcon={null}
                                renderInput={(params) => (
                                    <TextFieldInput
                                        {...params}
                                        label="Add people with their e-mail address"
                                        placeholder="Type to search..."
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const { onDelete } = getTagProps({ index });
                                        return (
                                            <CustomChip
                                                key={option.email}
                                                email={option.email}
                                                user={option}
                                                role={selectedRoles[option._id] || 'viewer'}
                                                onRoleChange={(userId, newRole) => {
                                                    setSelectedRoles(prev => ({
                                                        ...prev,
                                                        [userId]: newRole
                                                    }));
                                                }}
                                                onDelete={onDelete}
                                            />
                                        );
                                    })
                                }
                                PaperComponent={({ children }) => (
                                    <Paper sx={{
                                        backgroundColor: "#ECECEC",
                                        borderRadius: "10px",
                                        mt: 1,
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                    }}>{children}</Paper>
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                py: 1,
                                            }}
                                        >
                                            <Avatar
                                                alt={option.name}
                                                src={option.avatarUrl}
                                                sx={{ width: 36, height: 36 }}
                                            />
                                            <Box sx={{ ml: 2 }}>
                                                <Typography
                                                    margin="dense"
                                                    sx={{
                                                        lineHeight: 1.2,
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {option.name}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        lineHeight: 1,
                                                        display: 'block'
                                                    }}
                                                >
                                                    {option.email}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            />
                        </Grid2>
                        <Box sx={{ mt: 4, display: isMobile ? 'block' : 'flex', justifyContent: 'space-between' }}>
                            <AddButton
                                startIcon={
                                    <img
                                        src={ProfileAddIcon}
                                        alt="Provide access"
                                        style={{ width: 24, height: 24 }}
                                    />
                                }
                                label="Provide access"
                                sx={{
                                    fontSize: '16px',
                                    '&.Mui-disabled': {
                                        background: '#ACACAC',
                                        color: '#FFFFFF'
                                    },
                                    width: isMobile ? '100%' : '220px'
                                }}
                                disabled={selectedUsers.length === 0}
                                onClick={handleAddUsers}
                            />
                            <OutlinedAddButton
                                startIcon={
                                    <img
                                        src={copyLinkIcon}
                                        alt="Copy Link Icon"
                                        style={{ width: 24, height: 24 }}
                                    />
                                }
                                color="white"
                                label="Copy link"
                                sx={{ fontSize: '16px', border: "1.5px solid", mt: isMobile ? 1 : 0, width: isMobile ? '100%' : '175px' }}
                                onClick={onClose}
                            />
                        </Box>
                        <Typography sx={{ mt: 6 }}>
                            <span className="font-normal">People with access </span>
                        </Typography>
                        <Box sx={{ mt: 2, maxHeight: 200, overflowY: 'auto' }}>
                            {[...currentType?.user_details || []]
                                .sort((a, b) => {
                                    if (a.role?.role === 'admin') return -1;
                                    if (b.role?.role === 'admin') return 1;
                                    return 0;
                                })
                                .map((user, index) => (
                                    <Box key={index}>
                                    <Grid2
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            py: 1,
                                            width: isMobile ? '400px' : '100%'
                                        }}
                                    >
                                        <Avatar
                                            alt={user.name}
                                            src={user.avatarUrl}
                                            sx={{ width: 36, height: 36 }}
                                        />
                                        <Box sx={{ ml: 2, flexGrow: 1 }}>
                                            <Typography margin="dense" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                                                {user.name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ lineHeight: 1, display: 'block' }}>
                                                {user.email}
                                            </Typography>
                                        </Box>
                                        {user.role?.role === 'admin' ? (
                                            <Typography sx={{ fontSize: "12px", mr: 4 }}>
                                                {user.role.role}
                                            </Typography>
                                        ) : (
                                            <Select
                                                value={user.role?.role || 'viewer'}
                                                onChange={(e) => handleRoleChange(user._id, e.target.value)}
                                                sx={{
                                                    mr: 2,
                                                    fontSize: '12px',
                                                    '& .MuiSelect-select': { py: 0.5, px: 1 }
                                                }}
                                            >
                                                <MenuItem
                                                    value="viewer"
                                                    sx={{
                                                        fontSize: '0.813rem',
                                                        minHeight: '28px',
                                                        color: '#757575',
                                                        py: 0.5
                                                    }}
                                                >
                                                    View
                                                </MenuItem>
                                                <MenuItem
                                                    value="editor"
                                                    sx={{
                                                        fontSize: '0.813rem',
                                                        minHeight: '28px',
                                                        color: '#757575',
                                                        py: 0.5
                                                    }}
                                                >
                                                    Edit
                                                </MenuItem>
                                            </Select>
                                        )}
                                        {environments.length > 1 && (
                                            <Tooltip title="Access per environment">
                                                <Typography
                                                    onClick={() =>
                                                        setExpandedUser(expandedUser === user._id ? null : user._id)
                                                    }
                                                    sx={{
                                                        fontSize: '12px',
                                                        mr: 2,
                                                        cursor: 'pointer',
                                                        color: '#4f46e5',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    Environments
                                                    <KeyboardArrowDownIcon
                                                        sx={{
                                                            fontSize: 16,
                                                            transition: 'transform .2s',
                                                            transform:
                                                                expandedUser === user._id ? 'rotate(180deg)' : 'none',
                                                        }}
                                                    />
                                                </Typography>
                                            </Tooltip>
                                        )}
                                        <Typography
                                            onClick={() => (user.role?.role !== 'admin' || (  user.role?.role == 'admin' && user.role?.user !== currentType.createdBy?.[0]._id )) && handleRemoveUserPopup(user)}
                                            sx={{
                                                fontSize: "12px",
                                                color: user.role?.role === 'admin' &&  user.role?.user == currentType.createdBy?.[0]._id  ? '#ACACAC' : '#960101',
                                                cursor: user.role?.role === 'admin' && user.role?.user == currentType.createdBy?.[0]._id  ? 'not-allowed' : 'pointer',
                                                pointerEvents: user.role?.role === 'admin' && user.role?.user == currentType.createdBy?.[0]._id  ? 'none' : 'auto'
                                            }}
                                        >
                                            Remove
                                        </Typography>
                                    </Grid2>

                                    {/* Per-environment overrides. "Inherit" is the default and
                                        means this member follows their workspace role here —
                                        which is how every membership behaved before environments
                                        existed, so an untouched workspace reads as all-inherit. */}
                                    <Collapse in={expandedUser === user._id} unmountOnExit>
                                        <Box
                                            sx={{
                                                ml: 7, mr: 2, mb: 1.5, p: 1.5,
                                                borderRadius: '10px',
                                                backgroundColor: '#FAFAFB',
                                                border: '1px solid #ECECEC',
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>
                                                Overrides this member&apos;s workspace role
                                                {` (${accessByUser[String(user._id)]?.role ?? user.role?.role ?? 'viewer'})`} in one environment.
                                                Choose &quot;No access&quot; to hide an environment from them. New environments inherit the workspace role.
                                            </Typography>

                                            {environments.map((environment) => {
                                                const record = accessByUser[String(user._id)];
                                                const override = record?.environmentRoles?.[environment.key];
                                                const effective =
                                                    record?.effective?.[environment.key] ??
                                                    record?.role ?? 'viewer';
                                                const busy = savingAccess === `${user._id}:${environment.key}`;

                                                return (
                                                    <Box
                                                        key={environment.key}
                                                        sx={{ display: 'flex', alignItems: 'center', py: 0.5, gap: 1 }}
                                                    >
                                                        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography sx={{ fontSize: '13px' }}>
                                                                {environment.name}
                                                            </Typography>
                                                            {environment.isDefault && (
                                                                <Chip
                                                                    label="live"
                                                                    size="small"
                                                                    sx={{ height: 16, fontSize: '10px', color: '#0f766e', backgroundColor: 'rgba(13,148,136,0.12)' }}
                                                                />
                                                            )}
                                                            {!override && (
                                                                <Typography variant="caption" sx={{ color: '#ACACAC' }}>
                                                                    inherited — {effective}
                                                                </Typography>
                                                            )}
                                                            {override === 'none' && (
                                                                <Typography variant="caption" sx={{ color: '#960101' }}>
                                                                    hidden from this member
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Select
                                                            size="small"
                                                            disabled={busy}
                                                            value={override ?? 'inherit'}
                                                            onChange={(e) =>
                                                                handleEnvironmentRoleChange(user._id, environment.key, e.target.value)
                                                            }
                                                            sx={{
                                                                minWidth: 120,
                                                                fontSize: '12px',
                                                                backgroundColor: '#fff',
                                                                '& .MuiSelect-select': { py: 0.5, px: 1 },
                                                            }}
                                                        >
                                                            <MenuItem value="inherit" sx={{ fontSize: '0.813rem' }}>
                                                                Inherit
                                                            </MenuItem>
                                                            <MenuItem value="viewer" sx={{ fontSize: '0.813rem' }}>View</MenuItem>
                                                            <MenuItem value="editor" sx={{ fontSize: '0.813rem' }}>Edit</MenuItem>
                                                            <MenuItem value="admin" sx={{ fontSize: '0.813rem' }}>Admin</MenuItem>
                                                            {/* Confines the member to their other environments. A new
                                                                environment still inherits the workspace role, so it has
                                                                to be set here too if they should not see it. */}
                                                            <MenuItem value="none" sx={{ fontSize: '0.813rem', color: '#960101' }}>No access</MenuItem>
                                                        </Select>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                    </Box>
                                ))}
                        </Box>
                    </Grid2>
                    <DeleteConfirmationModal
                        startIcon={<RemoveIcon />}
                        deleteButtonLabel={'Remove'}
                        open={deleteModalOpen}
                        onClose={() => setDeleteModalOpen(false)}
                        text={
                            <>
                                <span className="font-normal">Are you sure, you want to remove </span>
                                {`"${selectedDeleteUser[0]?.name}" `}<span className="font-normal"> from the workspace</span>
                                {` "${currentType?.name}" `}
                                ?
                            </>
                        }
                        onConfirm={handleDeleteUsers}
                    />
                </ModalBox>
            </Modal>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setOpenSnackbar(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >{snackbarMessage}</Alert>
            </Snackbar>
        </>
    );
};

// ManageUsersModal.propTypes = {
//     open: PropTypes.bool,
//     onClose: PropTypes.func,
//     selectedType: PropTypes.any,
//     onSuccess: PropTypes.func
// };


export default ManageUsersModal;